import type { Metadata } from "next";
import type { BaseProduct, Category, Page } from "../types";
import {
  generateCategoryAlternates,
  generatePageAlternates,
  generateProductAlternates,
  type CategoryAlternateOptions,
  type PageAlternateOptions,
  type ProductAlternateOptions,
} from "./alternates";

export interface GenerateProductMetadataOptions {
  product: BaseProduct;
  locale: string;
  alternates?: Omit<ProductAlternateOptions, "product">;
}

export function generateProductMetadata({
  product,
  locale,
  alternates: alternateOptions,
}: GenerateProductMetadataOptions): Metadata {
  const alternates = alternateOptions
    ? generateProductAlternates({ ...alternateOptions, product })
    : undefined;
  return {
    title: product.name,
    description: product.shortDescription,
    openGraph: {
      type: "website",
      url: alternates?.canonical?.toString() || product.uri,
      title: product.name,
      description: product.shortDescription,
      locale,
      images: product.media.map((media) => media.href),
    },
    alternates,
  };
}

export interface GenerateCategoryMetadataOptions {
  category: Category;
  alternates?: Omit<CategoryAlternateOptions, "category">;
  locale: string;
  description?: string;
}

export function generateCategoryMetadata({
  category,
  alternates: alternateOptions,
  locale,
  description,
}: GenerateCategoryMetadataOptions): Metadata {
  const alternates = alternateOptions
    ? generateCategoryAlternates({ ...alternateOptions, category })
    : undefined;

  return {
    title: category.name,
    description: category.name,
    openGraph: {
      type: "website",
      url: alternates?.canonical?.toString() || category.uri,
      title: category.name,
      description: description,
      locale,
    },
    alternates,
  };
}

export interface GeneratePageMetadataOptions {
  page: Page;
  alternates?: Omit<PageAlternateOptions, "page">;
  description?: string;
}

export interface PageMetadata {
  title?: string;
  og_image?: string;
  og_title?: string;
  description?: string;
  twitter_image?: string;
  twitter_title?: string;
  og_description?: string;
  twitter_description?: string;
}

export const hasPageMetadata = <T extends Page>(
  story: T
): story is T & {
  content: {
    metadata: Partial<PageMetadata>;
  };
} => {
  const metadata = (
    story as unknown as {
      content: {
        metadata?: Partial<PageMetadata>;
      };
    }
  )?.content?.metadata;

  return !!metadata && Object.keys(metadata).length > 0 && !!metadata.title;
};

export function generatePageMetadata({
  page,
  alternates: alternateOptions,
  description,
}: GeneratePageMetadataOptions) {
  const alternates = alternateOptions
    ? generatePageAlternates({ ...alternateOptions, page })
    : undefined;

  return {
    title: page.name,
    description: description,
    alternates,
  };
}
